import { categories, mainSiteUrl } from '@/mocks/categories';
import { useState, useRef, useEffect } from 'react';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <section className="relative w-full h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Modern%20organized%20creative%20desk%20workspace%20with%20open%20laptop%20showing%20colorful%20spreadsheet%20data%20on%20screen%2C%20steaming%20ceramic%20coffee%20mug%2C%20small%20potted%20succulent%20plant%2C%20fabric%20notebook%20with%20handwritten%20notes%2C%20brass%20desk%20lamp%2C%20natural%20daylight%20from%20large%20window%2C%20warm%20wood%20desk%20surface%2C%20sophisticated%20productive%20atmosphere%2C%20editorial%20home%20office%20photography%2C%20cozy%20yet%20professional%20startup%20aesthetic%2C%20soft%20bokeh%20background&width=1600&height=900&seq=hero-v2-main&orientation=landscape"
          alt="Superbuy Spreadsheet lifestyle and product photography"
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/35"></div>

      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 flex flex-col items-center text-center">
        <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-tight mb-6 max-w-4xl">
          Find Better Products Faster With The Ultimate Superbuy Spreadsheet
        </h2>
        <p className="text-base md:text-lg text-white/80 max-w-2xl mb-10 leading-relaxed">
          Access organized Superbuy Spreadsheet resources, product collections, buyer guides,
          QC photo explanations and expert comparison tools designed to simplify international shopping.
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-4 mb-12">
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all"
          >
            SUPERBUY SHEET
            <i className="ri-arrow-right-up-line"></i>
          </a>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full border-2 border-white/60 text-white font-semibold text-sm whitespace-nowrap cursor-pointer hover:bg-white/10 transition-all"
            >
              Browse Categories
              <i className={`ri-arrow-down-s-line transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-72 bg-background-50 rounded-lg overflow-hidden shadow-lg border border-background-200/70 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-900 transition-colors border-b border-background-100 last:border-b-0"
                  >
                    <span className="font-medium">{cat.displayName}</span>
                    <span className="block text-xs text-foreground-400 mt-0.5">{cat.description}</span>
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-6 text-white/70 text-xs md:text-sm">
          <span className="flex items-center gap-1.5">
            <i className="ri-check-line text-accent-500 text-base"></i>
            Updated Resources
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-check-line text-accent-500 text-base"></i>
            Safety-Focused Guides
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-check-line text-accent-500 text-base"></i>
            Detailed Comparisons
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-check-line text-accent-500 text-base"></i>
            Beginner Friendly
          </span>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:block">
        <a
          href="#categories"
          className="flex flex-col items-center gap-2 text-white/50 hover:text-white/80 transition-colors cursor-pointer"
        >
          <span className="text-xs font-medium">Scroll to explore</span>
          <i className="ri-arrow-down-line animate-bounce"></i>
        </a>
      </div>
    </section>
  );
}